/** Used when a preset has no slug — the id still has to be unique per run. */
const FALLBACK_SEGMENT = 'generation';

type RunIdParams = {
  slug?: string;
  timestamp: number;
};

const buildRunId = ({ slug, timestamp }: RunIdParams): string =>
  `${slug || FALLBACK_SEGMENT}-${timestamp}`;

/**
 * The identifier every file of one multi-output run carries, or `null` for a
 * single-output run, which needs no grouping.
 *
 * A preset whose flow declares `outputsCount > 1` gets an id; everything else
 * keeps behaving exactly as before. Scoped per **run**, not per preset: the
 * timestamp means a second run of the same preset groups on its own instead of
 * merging into the first.
 *
 * This is what the creations grid groups on — deliberately *not* the Drive
 * `parentFolderUid`, because every result shares the `GenAI` folder and
 * grouping on that would collapse the entire library into one bogus run. The id
 * travels in the drive attributes instead, so it survives a reload (see
 * `parsePresetAttributes`).
 *
 * Results are not foldered by run: `getDriveFiles` lists flattened and filters
 * by the `tool` attribute, so the folder never took part in the grouping.
 */
const createGenerationRun = ({
  outputsCount,
  slug,
  timestamp,
}: RunIdParams & {
  outputsCount?: number | null;
}): string | null => {
  if (!outputsCount || outputsCount <= 1) return null;

  return buildRunId({ slug, timestamp });
};

/**
 * A run id for a flow whose output count is not knowable before it answers.
 *
 * The `presetJsonUrl` deeplink mounts a pseudo-preset built from a remote
 * project JSON, and that JSON declares no `outputsCount` — the count is first
 * known from the poll response, which arrives long after the submit that has to
 * carry the id into the Drive attributes. So the id is minted unconditionally
 * there.
 *
 * Safe to do: grouping takes at least two files sharing an id
 * (`groupContentFilesByRun`), so a single-output run keeps rendering as an
 * ordinary tile and a lone `runId` shows no filmstrip.
 */
export const createUnknownOutputsRun = (params: RunIdParams): string =>
  buildRunId(params);

export default createGenerationRun;
